export class FuturonSerialMonitor {
  constructor(screenElement) {
    this.screen = screenElement;
    this.port = null;
    this.reader = null;
    this.writer = null;
    this.connected = false;
    this.keepReading = false;
    this.decoder = new TextDecoder();
    this.encoder = new TextEncoder();
    this.logHistory = [];
    this.lineBuffer = '';
    this.onLedStatusChange = null; // function('ON' | 'OFF' | 'UNKNOWN')
  }

  async connect(baudRate = 115200, customPort = null) {
    if (!('serial' in navigator)) {
      throw new Error('Web Serial API is not supported in this browser.');
    }

    try {
      if (customPort) {
        this.port = customPort;
      } else {
        const existingPorts = await navigator.serial.getPorts();
        if (existingPorts && existingPorts.length > 0) {
          this.port = existingPorts[0];
        } else {
          this.port = await navigator.serial.requestPort();
        }
      }

      await this.port.open({ baudRate: parseInt(baudRate, 10) });
      
      this.connected = true;
      this.keepReading = true;
      this.lineBuffer = '';
      
      this.appendLine(`[System] Connected to Serial Port at ${baudRate} baud. Listening for Serial.print() logs & replies...`, 'system-line');
      
      // Hardware reset chip on connect to trigger sketch setup() and Serial.print output
      await this.resetChip();

      // Start async read loop
      this.readLoop();
      return true;
    } catch (err) {
      this.appendLine(`[System] Serial connect error: ${err.message}`, 'error-line');
      await this.disconnect();
      throw err;
    }
  }

  async disconnect() {
    this.keepReading = false;
    if (this.reader) {
      try {
        await this.reader.cancel();
      } catch (e) { /* ignore */ }
    }

    if (this.port) {
      try {
        await this.port.close();
      } catch (e) { /* ignore */ }
    }

    this.port = null;
    this.reader = null;
    this.writer = null;
    this.connected = false;
    this.lineBuffer = '';
    this.appendLine('[System] Serial Port Disconnected.', 'system-line');
    if (this.onLedStatusChange) {
      this.onLedStatusChange('UNKNOWN');
    }
  }

  async readLoop() {
    while (this.port && this.port.readable && this.keepReading) {
      try {
        this.reader = this.port.readable.getReader();
        while (true) {
          const { value, done } = await this.reader.read();
          if (done) break;
          if (value) {
            const text = this.decoder.decode(value, { stream: true });
            this.handleIncomingStream(text);
          }
        }
      } catch (err) {
        if (this.keepReading) {
          this.appendLine(`[System] Read error: ${err.message}`, 'error-line');
        }
      } finally {
        if (this.reader) {
          try {
            this.reader.releaseLock();
          } catch (e) {}
          this.reader = null;
        }
      }
    }
  }

  handleIncomingStream(chunk) {
    this.lineBuffer += chunk;
    this.appendOutput(chunk);

    // Check for LED state patterns in incoming buffer
    const upperBuffer = this.lineBuffer.toUpperCase();

    const isLedOn = upperBuffer.includes('LED: ON') ||
                    upperBuffer.includes('LED IS ON') ||
                    upperBuffer.includes('LED STATUS: ON') ||
                    upperBuffer.includes('LED_ON') ||
                    upperBuffer.includes('LED:ON') ||
                    upperBuffer.includes('LED HIGH') ||
                    upperBuffer.includes('LED: HIGH') ||
                    upperBuffer.includes('PIN 13 HIGH') ||
                    upperBuffer.includes('PIN 13 IS HIGH');

    const isLedOff = upperBuffer.includes('LED: OFF') ||
                     upperBuffer.includes('LED IS OFF') ||
                     upperBuffer.includes('LED STATUS: OFF') ||
                     upperBuffer.includes('LED_OFF') ||
                     upperBuffer.includes('LED:OFF') ||
                     upperBuffer.includes('LED LOW') ||
                     upperBuffer.includes('LED: LOW') ||
                     upperBuffer.includes('PIN 13 LOW') ||
                     upperBuffer.includes('PIN 13 IS LOW');

    if (isLedOn) {
      if (this.onLedStatusChange) this.onLedStatusChange('ON');
    } else if (isLedOff) {
      if (this.onLedStatusChange) this.onLedStatusChange('OFF');
    }

    // Keep buffer bounded
    if (this.lineBuffer.length > 2000) {
      this.lineBuffer = this.lineBuffer.slice(-1000);
    }
  }

  async send(data, lineEnding = '\r\n') {
    if (!this.connected || !this.port || !this.port.writable) {
      throw new Error('Serial monitor port is not connected.');
    }

    try {
      const payload = data + lineEnding;
      const writer = this.port.writable.getWriter();
      await writer.write(this.encoder.encode(payload));
      writer.releaseLock();

      const timeStr = new Date().toLocaleTimeString();
      this.appendLine(`[TX] > ${data}`, 'input-line');
    } catch (err) {
      this.appendLine(`[System] Send error: ${err.message}`, 'error-line');
    }
  }

  async toggleRTS(state) {
    if (this.port) {
      try {
        await this.port.setSignals({ requestToSend: state });
        this.appendLine(`[System] RTS signal set to ${state}`, 'system-line');
      } catch (e) {}
    }
  }

  async toggleDTR(state) {
    if (this.port) {
      try {
        await this.port.setSignals({ dataTerminalReady: state });
        this.appendLine(`[System] DTR signal set to ${state}`, 'system-line');
      } catch (e) {}
    }
  }

  async resetChip() {
    if (this.port) {
      this.appendLine('[System] Resetting hardware chip (pulsing RTS/DTR to run sketch)...', 'system-line');
      try {
        await this.port.setSignals({ dataTerminalReady: true, requestToSend: true });
        await new Promise(r => setTimeout(r, 120));
        await this.port.setSignals({ dataTerminalReady: false, requestToSend: false });
        await new Promise(r => setTimeout(r, 50));
      } catch (e) {
        // Ignoring signal set error if unsupported
      }
    }
  }

  appendOutput(text) {
    if (!this.screen) return;
    this.logHistory.push(text);
    
    // Append text to terminal element
    const span = document.createElement('span');
    span.textContent = text;
    this.screen.appendChild(span);

    // Auto scroll
    this.screen.scrollTop = this.screen.scrollHeight;
  }

  appendLine(lineText, className = '') {
    if (!this.screen) return;
    const timeStr = new Date().toLocaleTimeString();
    const line = `[${timeStr}] ${lineText}\n`;
    this.logHistory.push(line);

    const div = document.createElement('div');
    if (className) div.className = className;
    div.textContent = line;
    this.screen.appendChild(div);
    this.screen.scrollTop = this.screen.scrollHeight;
  }

  clear() {
    if (this.screen) {
      this.screen.innerHTML = '';
    }
    this.logHistory = [];
    this.lineBuffer = '';
    if (this.onLedStatusChange) {
      this.onLedStatusChange('UNKNOWN');
    }
  }

  exportLog() {
    const blob = new Blob([this.logHistory.join('')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `futuron_serial_log_${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }
}
